/**
 * <s-product-gallery> — Product page gallery: one large snap-scrolling main viewer,
 * prev/next arrows, an image counter and a thumbnail strip.
 *
 * Commands (dispatched via commandfor/command on descendant buttons):
 *   --prev — Show the previous slide.
 *   --next — Show the next slide.
 *   --go   — Show the slide at the source button's data-index.
 *
 * Required child markup:
 *   [data-gallery-track]   — Horizontal snap-scroll container.
 *   [data-gallery-slide]   — One per media item, inside the track.
 *   [data-gallery-thumb]   — Optional thumbnail buttons, in slide order.
 *   [data-gallery-current] — Optional element whose text shows the 1-based index.
 *   [data-gallery-prev] / [data-gallery-next] — Optional arrows; disabled at the ends.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #track;
  #slides = [];
  #thumbs = [];
  #index = 0;
  #frame = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;
    this.#slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.#thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.addEventListener(
      'command',
      (event) => {
        if (event.command === '--prev') this.#go(this.#index - 1);
        else if (event.command === '--next') this.#go(this.#index + 1);
        else if (event.command === '--go') this.#go(Number(event.source?.dataset.index || 0));
      },
      { signal },
    );

    this.#track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(this.#frame);
        this.#frame = requestAnimationFrame(() => this.#syncFromScroll());
      },
      { signal, passive: true },
    );

    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #syncFromScroll() {
    const width = this.#track.clientWidth || 1;
    // Math.abs keeps the index correct in RTL, where scrollLeft runs negative.
    const index = Math.round(Math.abs(this.#track.scrollLeft) / width);
    if (index !== this.#index) {
      this.#index = Math.min(Math.max(index, 0), this.#slides.length - 1);
      this.#update();
    }
  }

  #go(index) {
    if (!this.#slides.length) return;
    const next = Math.min(Math.max(index, 0), this.#slides.length - 1);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const slide = this.#slides[next];
    // The track is position: relative, so offsetLeft is measured from the track's own edge.
    const isRtl = getComputedStyle(this.#track).direction === 'rtl';
    const left = isRtl
      ? -(this.#track.scrollWidth - slide.offsetLeft - slide.offsetWidth)
      : slide.offsetLeft;
    this.#track.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
    this.#index = next;
    this.#update();
  }

  #update() {
    this.#thumbs.forEach((thumb, i) => {
      if (i === this.#index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    const current = this.querySelector('[data-gallery-current]');
    if (current) current.textContent = String(this.#index + 1);
    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev) prev.toggleAttribute('disabled', this.#index === 0);
    if (next) next.toggleAttribute('disabled', this.#index >= this.#slides.length - 1);
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', SProductGallery);
